import { IndicatorState } from '@/types';
import { getIndicator } from '@/services/indicators';

interface StatusIconProps {
  state: IndicatorState;
  /** Tailwind size utilities, e.g. "w-5 h-5". */
  className?: string;
}

/**
 * The shape half of every status signal.
 *
 * Each state gets a genuinely different OUTLINE — circle, triangle, octagon,
 * dashed circle — so the icon still reads at a glance in greyscale, at small
 * sizes, or to a reader who cannot separate green from amber. Filling four
 * identical circles with four different colours, which is what most status
 * pages ship, fails all three.
 *
 * The SVG is `aria-hidden`: it is decoration on top of a status WORD that is
 * always present in the markup beside it, so screen readers get the word and
 * never a duplicate.
 */
export function StatusIcon({ state, className = 'w-4 h-4' }: StatusIconProps) {
  const { text } = getIndicator(state);
  const shared = {
    className: `${className} ${text} shrink-0`,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (state) {
    case 'operational':
      // Circle + check: closed, settled, nothing sticking out.
      return (
        <svg {...shared}>
          <circle cx="8" cy="8" r="6.5" />
          <path d="M5.2 8.3l2 2 3.6-4.2" />
        </svg>
      );

    case 'degraded':
      // Triangle: the one shape with a point on top. Reads as "caution" with
      // no colour at all.
      return (
        <svg {...shared}>
          <path d="M8 1.9 15 14H1z" />
          <path d="M8 6.4v3.1" />
          <path d="M8 11.6h.01" />
        </svg>
      );

    case 'outage':
      // Octagon + cross: the stop sign. Distinct from the circle even at 12px.
      return (
        <svg {...shared}>
          <path d="M5.5 1.5h5l3.5 3.5v5L10.5 14.5h-5L1.5 10.5v-5z" />
          <path d="M5.9 5.9l4.2 4.2" />
          <path d="M10.1 5.9l-4.2 4.2" />
        </svg>
      );

    case 'unknown':
      // Broken circle + query: visibly INCOMPLETE, so an unreadable feed looks
      // unfinished rather than fine.
      return (
        <svg {...shared} strokeDasharray="2.6 2.4">
          <circle cx="8" cy="8" r="6.5" />
          <path d="M6.1 6.2a2 2 0 013.1 1.6c0 1-1.2 1.3-1.2 2.2" strokeDasharray="0" />
          <path d="M8 11.8h.01" strokeDasharray="0" />
        </svg>
      );
  }
}

/**
 * The "we are still finding out" indicator.
 *
 * Separate from StatusIcon because connecting is not a verdict: it must not be
 * able to appear anywhere a verdict is expected.
 */
export function ConnectingIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg
      className={`${className} text-slate-400 shrink-0 animate-spin`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="8" cy="8" r="6.5" className="opacity-25" />
      <path d="M14.5 8A6.5 6.5 0 008 1.5" />
    </svg>
  );
}
